import { useState } from 'react';

export default function HomePage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate required fields
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const message = formData.get('message') as string;

    if (!name || !email || !message) {
      setFormStatus('error');
      setFormMessage('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setFormStatus('error');
      setFormMessage('Please enter a valid email address');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d5sakn399ei0h7kmafog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        setFormMessage('Thank you for your message! We will get back to you soon.');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setFormStatus('error');
      setFormMessage('Failed to send message. Please try again later.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <header className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1f3a] via-[#0d1117] to-[#1a1f3a]"></div>
        
        {/* Company Name at Top */}
        <div className="absolute top-12 left-12 z-20">
          <img 
            src="https://public.readdy.ai/ai/img_res/ce15ca42-7149-4e1a-8412-404dd42ee748.png" 
            alt="Serfasa S.A. Logo" 
            className="h-16 w-auto object-contain"
          />
        </div>

        {/* Decorative Shapes */}
        <div className="absolute right-0 top-0 w-2/5 h-full bg-[#f5f3f0]">
          <div className="absolute top-1/3 left-1/4 w-64 h-80 bg-[#ff6b6b] rounded-[32px] transform rotate-8 opacity-90"></div>
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-[#4ecdc4] rounded-full opacity-80"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="w-64 h-[500px] border-4 border-white/20 rounded-[40px] transform rotate-12"></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-12 w-full">
          <div className="max-w-2xl">
            <h1 className="mb-8">
              <div className="text-7xl font-extralight text-white/40 leading-tight">Expert iOS</div>
              <div className="text-8xl font-extrabold text-white leading-tight">Development</div>
              <div className="text-7xl font-medium bg-gradient-to-r from-white to-[#00d4ff] bg-clip-text text-transparent leading-tight">Solutions</div>
            </h1>
            <p className="text-[#b8c1d9] text-lg leading-relaxed max-w-md">
              Crafting premium applications that redefine mobile security and user experience on iOS platform
            </p>
          </div>
        </div>
      </header>

      {/* About Section */}
      <section id="about" className="bg-white py-32">
        <div className="max-w-7xl mx-auto px-20">
          <div className="grid grid-cols-12 gap-24">
            {/* Left Column */}
            <div className="col-span-7">
              <div className="text-xs uppercase tracking-widest text-[#6b7280] mb-12">WHO WE ARE</div>
              <h2 className="mb-10">
                <div className="text-5xl font-bold text-[#1f2937] leading-tight">A team of experts</div>
                <div className="text-5xl font-extrabold text-[#1f2937] leading-tight">developing modern</div>
                <div className="text-5xl font-bold text-[#1f2937] leading-tight relative inline-block">
                  applications
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#ff6b6b] to-[#ff8c42]"></div>
                </div>
              </h2>
              <div className="space-y-6 max-w-lg">
                <p className="text-[#4b5563] text-base leading-relaxed">
                  Serfasa S.A. brings together a dedicated team of iOS specialists who are passionate about creating exceptional mobile experiences. Our expertise spans across security, performance optimization, and intuitive user interface design.
                </p>
                <p className="text-[#4b5563] text-base leading-relaxed">
                  With a singular focus on the iOS platform, we deliver applications that not only meet but exceed the high standards expected by Apple users worldwide. Every product we develop is crafted with precision, security, and user satisfaction at its core.
                </p>
              </div>
            </div>

            {/* Right Column */}
            <div className="col-span-5 pt-20">
              <div className="bg-gradient-to-br from-[#e0f2fe] to-white rounded-3xl p-16 shadow-sm">
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-8">
                  <i className="ri-apple-fill text-6xl text-[#1e40af]"></i>
                </div>
                <div className="text-center mb-12">
                  <div className="text-3xl font-semibold text-[#1e40af] mb-2">iOS Focused</div>
                  <div className="text-sm text-[#6b7280]">100% Platform Dedication</div>
                </div>
                <div className="h-px bg-gradient-to-r from-transparent via-[#1e40af]/20 to-transparent mb-12"></div>
                <div className="text-center">
                  <div className="text-3xl font-semibold text-[#1e40af] mb-2">Security First</div>
                  <div className="text-sm text-[#6b7280]">Advanced Protection Standards</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="bg-[#fafafa] py-32">
        <div className="max-w-7xl mx-auto px-20">
          {/* Section Header */}
          <div className="text-center mb-20">
            <div className="text-xs uppercase tracking-widest text-[#ff6b6b] mb-4">OUR PRODUCTS</div>
            <h2>
              <div className="text-6xl font-extralight text-black leading-tight">Enhancing Your</div>
              <div className="text-7xl font-extrabold text-black leading-tight">Mobile Experience</div>
            </h2>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-3 gap-8">
            {/* VPN Product Card */}
            <div className="bg-white rounded-3xl p-12 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-[#8b5cf6] to-[#3b82f6] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-4xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-black mb-5">VPN</h3>
              <ul className="space-y-3 mb-6">
                <li className="text-[#6b7280] text-base flex items-start">
                  <span className="mr-2">•</span>
                  <span>Enhanced privacy protection</span>
                </li>
                <li className="text-[#6b7280] text-base flex items-start">
                  <span className="mr-2">•</span>
                  <span>Secure connection encryption</span>
                </li>
                <li className="text-[#6b7280] text-base flex items-start">
                  <span className="mr-2">•</span>
                  <span>Global server network</span>
                </li>
              </ul>
              <div className="h-0.5 bg-gradient-to-r from-[#8b5cf6] to-[#3b82f6]"></div>
            </div>

            {/* Cleaner Product Card */}
            <div className="bg-white rounded-3xl p-12 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-[#10b981] to-[#06b6d4] rounded-full flex items-center justify-center mb-6">
                <i className="ri-brush-line text-4xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-black mb-5">Cleaner</h3>
              <ul className="space-y-3 mb-6">
                <li className="text-[#6b7280] text-base flex items-start">
                  <span className="mr-2">•</span>
                  <span>Optimize device storage</span>
                </li>
                <li className="text-[#6b7280] text-base flex items-start">
                  <span className="mr-2">•</span>
                  <span>Remove unnecessary files</span>
                </li>
                <li className="text-[#6b7280] text-base flex items-start">
                  <span className="mr-2">•</span>
                  <span>Boost performance speed</span>
                </li>
              </ul>
              <div className="h-0.5 bg-gradient-to-r from-[#10b981] to-[#06b6d4]"></div>
            </div>

            {/* Performance Feature Card */}
            <div className="bg-white rounded-3xl p-12 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
              <div className="absolute top-8 right-8 text-[120px] font-extralight text-[#ff6b6b]/10 leading-none">02</div>
              <div className="relative z-10">
                <h3 className="text-3xl font-bold text-black mb-4">Optimized Performance</h3>
                <p className="text-[#6b7280] text-base leading-relaxed">
                  Our applications are engineered to deliver exceptional speed and efficiency, ensuring smooth operation even on older iOS devices.
                </p>
              </div>
            </div>

            {/* Security Focus Card */}
            <div className="bg-white rounded-3xl p-12 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center gap-6">
              <div className="flex-shrink-0">
                <i className="ri-lock-2-line text-6xl text-[#ff6b6b]"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-black mb-2">Security Focus</h3>
                <p className="text-[#6b7280] text-sm leading-relaxed">
                  Built with industry-leading security protocols to protect your data
                </p>
              </div>
            </div>

            {/* User Experience Card */}
            <div className="bg-white rounded-3xl p-12 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="text-[96px] font-serif text-[#e5e7eb] leading-none mb-6">"</div>
              <p className="text-[#4b5563] text-base leading-relaxed mb-6">
                Every feature is designed with the end user in mind, creating intuitive interfaces that feel natural and responsive.
              </p>
              <div className="text-xs uppercase tracking-wider text-[#9ca3af]">USER EXPERIENCE</div>
            </div>

            {/* Innovation Card */}
            <div className="bg-white rounded-3xl p-12 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
              <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-[#4ecdc4]/20 rounded-full"></div>
              <div className="absolute -right-4 -top-4 w-24 h-24 bg-[#ff6b6b]/20 rounded-full"></div>
              <div className="relative z-10">
                <h3 className="text-3xl font-bold text-black mb-4">Innovation</h3>
                <p className="text-[#6b7280] text-base leading-relaxed">
                  Constantly evolving with the latest iOS technologies and best practices
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-gradient-to-br from-[#1f2937] to-[#111827] py-40">
        <div className="max-w-6xl mx-auto px-20">
          {/* Contact Information Grid */}
          <div className="grid grid-cols-3 gap-12 mb-16">
            {/* Location */}
            <div>
              <div className="text-xs uppercase tracking-wider text-[#9ca3af] mb-5 flex items-center gap-2">
                <i className="ri-map-pin-line text-base text-[#ff6b6b]"></i>
                LOCATION
              </div>
              <div className="text-white text-base leading-relaxed space-y-1">
                <div>Avenida 13 E Norte 44 Local No.1</div>
                <div>City Panama</div>
                <div>Zip 0819</div>
                <div>Country Panama</div>
              </div>
            </div>

            {/* Email */}
            <div>
              <div className="text-xs uppercase tracking-wider text-[#9ca3af] mb-5 flex items-center gap-2">
                <i className="ri-mail-line text-base text-[#ff6b6b]"></i>
                EMAIL
              </div>
              <a 
                href="mailto:help@serfasasa.com" 
                className="text-white text-lg hover:text-[#ff6b6b] transition-colors duration-200 inline-block hover:underline"
              >
                help@serfasasa.com
              </a>
            </div>

            {/* Phone */}
            <div>
              <div className="text-xs uppercase tracking-wider text-[#9ca3af] mb-5 flex items-center gap-2">
                <i className="ri-phone-line text-base text-[#ff6b6b]"></i>
                PHONE
              </div>
              <a 
                href="tel:+15856562584" 
                className="text-white text-lg hover:text-[#ff6b6b] transition-colors duration-200 inline-block"
              >
                +1 585 656 2584
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="mt-20 max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h3 className="text-4xl font-bold text-white mb-3">Get In Touch</h3>
              <p className="text-[#9ca3af] text-base">Have a question or want to work together? Send us a message.</p>
            </div>
            
            <form 
              id="contact_form"
              data-readdy-form 
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-[#9ca3af] mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-3 bg-[#374151] border border-[#4b5563] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#ff6b6b] focus:border-transparent transition-all"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#9ca3af] mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 bg-[#374151] border border-[#4b5563] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#ff6b6b] focus:border-transparent transition-all"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#9ca3af] mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 bg-[#374151] border border-[#4b5563] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#ff6b6b] focus:border-transparent transition-all resize-none"
                  placeholder="Your message (max 500 characters)"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full px-8 py-4 bg-[#ff6b6b] text-white font-semibold rounded-lg hover:bg-[#ff5252] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
              </button>

              {formStatus === 'success' && (
                <div className="p-4 bg-[#10b981]/20 border border-[#10b981] rounded-lg text-[#10b981] text-sm">
                  {formMessage}
                </div>
              )}

              {formStatus === 'error' && (
                <div className="p-4 bg-[#ef4444]/20 border border-[#ef4444] rounded-lg text-[#ef4444] text-sm">
                  {formMessage}
                </div>
              )}
            </form>
          </div>

          {/* Bottom Branding */}
          <div className="mt-32">
            <div className="h-px bg-[#374151] mb-12"></div>
            <div className="flex items-end justify-between">
              <div className="text-8xl font-extrabold text-[#2d3748] tracking-tight">
                Serfasa S.A.
              </div>
              <div className="text-sm text-[#6b7280] pb-2">
                © 2025 All rights reserved
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
